import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha, keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useAtomValue } from 'jotai';

import { fullscreenLoaderAtom } from './atom';

export { fullscreenLoaderAtom } from './atom';
export type { IFullscreenLoaderAtom } from './atom';
export { useFullscreenLoader } from './service';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const FullscreenLoader = () => {
  const config = useAtomValue(fullscreenLoaderAtom);

  if (!config) {
    return null;
  }

  return (
    <Backdrop
      open
      sx={{
        zIndex: theme => theme.zIndex.modal + 1,
        bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.6)'),
        backdropFilter: 'blur(8px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2.5,
          animation: `${fadeIn} 0.3s ease-out`,
        }}
      >
        <Box
          sx={theme => ({
            width: 56,
            height: 56,
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.08)}, transparent)`,
              backgroundSize: '200% 100%',
              animation: `${shimmer} 1.8s ease-in-out infinite`,
            },
          })}
        >
          <Box
            sx={theme => ({
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: `2.5px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              borderTopColor: theme.palette.primary.main,
              animation: 'spin 0.8s linear infinite',
              '@keyframes spin': {
                to: { transform: 'rotate(360deg)' },
              },
            })}
          />
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>
            {config.title}
          </Typography>
          {config.subtitle && (
            <Typography variant="caption" color="text.secondary">
              {config.subtitle}
            </Typography>
          )}
        </Box>

        <LinearProgress
          sx={theme => ({
            width: 180,
            height: 3,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '& .MuiLinearProgress-bar': {
              borderRadius: 2,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.light, 0.8)})`,
            },
          })}
        />
      </Box>
    </Backdrop>
  );
};
